/**
 * Clear all menu item photos: remove image URLs from Firestore and delete files from Firebase Storage.
 * Use this to start from scratch with new uploads (e.g. low-quality photos).
 */
import { ref, listAll, deleteObject } from 'firebase/storage';
import { getFirebaseStorage } from './firebase';
import { menuItemsService } from './database';

const STORAGE_PREFIX = 'menu-items';

/**
 * Recursively delete all files under a Storage reference; returns count deleted.
 */
async function deleteAllInRef(storageRef: ReturnType<typeof ref>): Promise<number> {
  const result = await listAll(storageRef);
  await Promise.all(result.items.map((itemRef) => deleteObject(itemRef)));
  let count = result.items.length;
  for (const prefixRef of result.prefixes) {
    count += await deleteAllInRef(prefixRef);
  }
  return count;
}

/**
 * 1. Clear imageUrl and imageUrlThumb for every menu item in Firestore.
 * 2. Delete all files under Storage path menu-items/.
 * Call refreshMenuItems() after this so the app state updates.
 */
export async function clearAllMenuPhotos(): Promise<{ menuItemsUpdated: number; filesDeleted: number }> {
  const storage = getFirebaseStorage();
  const menuItems = await menuItemsService.getAll();

  // Clear image fields for every menu item
  for (const item of menuItems) {
    await menuItemsService.update(item.id, { imageUrl: '', imageUrlThumb: '' });
  }

  let filesDeleted = 0;
  try {
    const rootRef = ref(storage, STORAGE_PREFIX);
    filesDeleted = await deleteAllInRef(rootRef);
  } catch (err) {
    // If folder is empty or doesn't exist, listAll may throw; that's ok
    console.warn('Storage cleanup:', err);
  }

  return { menuItemsUpdated: menuItems.length, filesDeleted };
}
